import "./Inbox.css";
import React, { useEffect, useState, useRef } from "react";
import InboxInvoiceList from "./InboxList/InboxInvoiceList";
import InvoiceView from "./InvoiceView/InvoiceView";
import { IoSettingsSharp } from "react-icons/io5";
import { GetInvoicesService_NEW } from "../../../Services/InvoiceService/InvoiceService";
import { useSelector } from "react-redux";
import RefreshBtn from "../../../Components/CustomButtons/RefreshBtn/RefreshBtn";
import { InvoiceFieldMap } from "../../../Services/InvoiceService/constants";
import ColumnSettings from "../../../Components/ColumnFilter/ColumnFilter";
import FilterBtn from "../../../Components/CustomButtons/FilterBtn/FilterBtn";
import Loader from "../../../Components/Loader/Loader";
import { InvoiceFilter } from "../../../Components/InvoiceFilter";
import { MdOutlineFilterAlt } from "react-icons/md";
import AdvancedSearch from "../../../Components/InvoiceSearch/AdvancedSearch";
import { toInvoiceSearchPayload } from "../../../Components/InvoiceSearch/AdvanceType";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import { InvoiceFilterFieldsPayload } from "../../../Components/InvoiceFilter/InvoiceFilter.fields";
import { ActiveFilterBar } from "../../../Components/InvoiceFilter/ActiveFilterBar";
import ToolsMenu from "./Tools/Toolsmenu";
import {
  UpdateInvoiceFlagService,
  DeleteInvoice,
  UnmergeInvoiceService,
} from "../../../Services/InvoiceService/InvoiceService";
import AlertMessage from "../../../Components/AlertMessage/AlertMessage";
import { useSearchParams } from "react-router-dom";
import { setSelectedInvoice } from "../../../Reducer/SelectedInvoiceReducer";
import { useDispatch } from "react-redux";
import SelectedClientCard from "../../../Components/SelectedClientCard/SelectedClientCard";
import { BulkEditOptions } from "../3Inbox/BulkeditPopup/BulkEditPopup"; // adjust path
import { BulkEditResult } from "./Tools/Toolsmenu";

// --- Types ---
interface RowData {
  id: number;
  status?: string;
  user?: string;
  date?: string;
  supplier?: string;
  category?: string;
  total?: number;
  selected?: boolean;
  description?: string;
  taxAmount?: number;
}

// --- Constants ---
const INVOICE_PER_FETCH = 100;
const STAGE_IDS = [11, 12, 13, 21, 22, 24];
const ARCHIVE_STATUSES = ["UnArchived", "Default"];

const Inbox: React.FC = () => {
  // --- Redux ---
  const selectedClient = useSelector((state: any) => state.selectedClient);
  const selectedClient_userID = selectedClient.selectedClient?.userId;

  // --- UI State ---
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [invoiceView, setInvoiceView] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);

  // --- Data State ---
  const [invoices, setInvoices] = useState<any[]>([]);
  const [totalInvoiceCount, setTotalInvoiceCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [activeFilters, setActiveFilters] = useState<any>(null);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [failedView, setFailedView] = useState(false);

  // --- Refs (not in state — updates must be instant, no re-render needed) ---
  const cursorRef = useRef<string>("");
  const sortKeyRef = useRef<keyof typeof InvoiceFieldMap | null>(null);
  const sortReverseRef = useRef<boolean>(false);

  const [searchTerm, setSearchTerm] = useState("");
  const debounceRef = useRef<any>(null);

  const activeFiltersRef = useRef<any>(null);
  const activeAdvancedSearchRef = useRef<any>(null);

  const invoicesRef = useRef<any[]>([]);

  const [activeAdvancedSearch, setActiveAdvancedSearch] = useState<any>(null);
  const advancedSearchRef = useRef<{ resetForm: () => void }>(null);
  interface SortState {
    key: keyof typeof InvoiceFieldMap | "default" | null;
    direction: boolean;
  }
  const savedSort = localStorage.getItem("sortSettings");
  const [sortSettings, setSortSettings] = useState<SortState>({
    key: "default",
    direction: false,
  });

  const [fullinvoices, setFullInvoices] = useState<any[]>([]);

  const [isOpenAdvance, setIsOpenAdvance] = useState(false);
  const DEFAULT_COLUMNS = [
    "selected",
    "ID",
    "Status",
    "Submitted By",
    "Date",
    "Supplier",
    "Total",
    "Tax",
    "Document Ref",
    "Description",
  ];

  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem("selectedColumns");
    return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
  });

  // --- Selection Handlers (passed down to InboxInvoiceList) ---
  const handleRowSelect = (id: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((sid) => sid !== id),
    );
  };
  useEffect(() => {
    invoicesRef.current = invoices;
  }, [invoices]);

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? invoicesRef.current.map((inv) => inv.id) : []);
  };

  // Clear selection whenever invoices list refreshes
  const clearSelection = () => setSelectedIds([]);

  const handleMerge = async (sourceIds: number[], mergedInvoiceId: number) => {
    // Optimistic update: immediately remove source invoices from inbox
    // User sees them disappear right away without waiting for a refetch
    setInvoices((prev) => prev.filter((inv) => !sourceIds.includes(inv.id)));
    setTotalInvoiceCount((prev) => prev - sourceIds.length);

    // Fetch the new merged invoice and add it to the top of the inbox
    try {
      const result = await GetInvoicesService_NEW({
        userId: selectedClient_userID,
        ids: [mergedInvoiceId],
      } as any);

      const newInvoice = result?.Items?.[0];
      if (newInvoice) {
        setInvoices((prev) => [newInvoice, ...prev]);
        setTotalInvoiceCount((prev) => prev + 1);
      }
    } catch {
      // If the targeted fetch fails, fall back to a full refresh
      await refreshInvoices();
    }

    clearSelection();
    AlertMessage({
      type: "success",
      text: `${sourceIds.length} invoices merged successfully`,
    });
  };

  // ─── Add handleUnmerge ────────────────────────────────────────────────────────

  const handleUnmerge = async (
    mergedInvoiceId: number,
    restoredIds: number[],
  ) => {
    // Remove the merged invoice from inbox immediately
    setInvoices((prev) => prev.filter((inv) => inv.id !== mergedInvoiceId));
    setTotalInvoiceCount((prev) => prev - 1);

    // Fetch the restored source invoices and add them back to the inbox
    try {
      const result = await GetInvoicesService_NEW({
        userId: selectedClient_userID,
        ids: restoredIds,
      } as any);

      const restored = result?.Items ?? [];
      if (restored.length > 0) {
        setInvoices((prev) => [...restored, ...prev]);
        setTotalInvoiceCount((prev) => prev + restored.length);
      } else {
        // Fallback — if targeted fetch returns nothing, do a full refresh
        await refreshInvoices();
      }
    } catch {
      await refreshInvoices();
    }
    clearSelection();
    AlertMessage({ type: "success", text: "Invoice unmerged successfully" });
  };

  const handleFlag = async (ids: number[]) => {
    // Flag all selected invoices (set isFlagged = true)
    try {
      await Promise.all(ids.map((id) => UpdateInvoiceFlagService(id, true)));
      AlertMessage({
        type: "success",
        text: "Invoice(s) flagged successfully",
      });
      // Refresh so the flag icons update from the server
      await refreshInvoices();
    } catch {
      AlertMessage({ type: "error", text: "Failed to flag invoice(s)" });
    }
  };

  const handleUnflag = async (ids: number[]) => {
    // Unflag all selected invoices (set isFlagged = false)
    try {
      await Promise.all(ids.map((id) => UpdateInvoiceFlagService(id, false)));
      AlertMessage({
        type: "success",
        text: "Invoice(s) unflagged successfully",
      });
      await refreshInvoices();
    } catch {
      AlertMessage({ type: "error", text: "Failed to unflag invoice(s)" });
    }
  };

  const handleDelete = async (ids: number[]) => {
    try {
      await Promise.all(ids.map((id) => DeleteInvoice(id)));
      // Remove deleted rows from local state immediately (optimistic)
      setInvoices((prev) => prev.filter((inv) => !ids.includes(inv.id)));
      setTotalInvoiceCount((prev) => prev - ids.length);
      clearSelection();
    } catch {
      AlertMessage({ type: "error", text: "Failed to delete invoice(s)" });
    }
  };

  const handleBulkEditComplete = async (result: BulkEditResult) => {
  const failedIds = result.failed.map((f) => f.id);
 
  // Re-fetch so edited values come from the server. refreshInvoices() keeps
  // the active filters, advanced search and sort, and reloads as many rows as
  // are currently loaded.
  await refreshInvoices();
 
  // Success: clear the selection like the other actions do.
  // Partial failure: leave only the failed rows checked so the user can retry them.
  setSelectedIds(failedIds);
};

const handleViewFailedItems = async (ids: number[]) => {
  try {
    const result = await GetInvoicesService_NEW({
      userId: selectedClient_userID,
      ids,
    } as any);
 
    setInvoices(result?.Items ?? []);
    setTotalInvoiceCount(result?.TotalItem ?? ids.length);
    setHasMore(false);
    cursorRef.current = "";
    setSelectedIds(ids);
    setFailedView(true);
  } catch {
    AlertMessage({ type: "error", text: "Failed to load the failed items" });
  }
};
 
const handleShowAllAgain = async () => {
  setFailedView(false);
  setSelectedIds([]);
  setHasMore(true);
  cursorRef.current = "";
  // Default limit (100), not invoices.length, because the list is currently tiny
  await FetchInvoices(false, false, null, activeFilters, activeAdvancedSearch);
};

  const bulkEditOptions: BulkEditOptions = {
    type: [ /* { value: "Invoice", label: "Invoice" }, ... */],
    supplier: [ /* from the same source as the edit form */],
    category: [ /* ... */],
    currency: [ /* { value: "LKR", label: "LKR" }, ... */],
    paid: [{ value: "true", label: "Yes" }, { value: "false", label: "No" }],
    paymentMethod: []
  };

 

  // --- Use Effects ---
  useEffect(() => {
    if (selectedClient_userID) FetchInvoices(false);
  }, []);

  useEffect(() => {
    if (selectedClient_userID) FetchInvoices(false, false, null, activeFilters);
  }, [activeFilters]);

  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  useEffect(() => {
    activeAdvancedSearchRef.current = activeAdvancedSearch;
  }, [activeAdvancedSearch]);

  // --- Data Fetching ---
  const FetchInvoices = async (
    loadMore = false,
    reverse: boolean = false,
    key: keyof typeof InvoiceFieldMap | null = null,
    filters: any = activeFilters,
    searchFields: any = null,
    limit: number = INVOICE_PER_FETCH,
    after: string | null = null,
  ) => {
    if (!selectedClient_userID) return;

    const apiSearchFields = toInvoiceSearchPayload(searchFields);

    try {
      if (!loadMore) setLoading(true); // Initial Fetch

      const result = await GetInvoicesService_NEW({
        userId: selectedClient_userID,
        after: after || cursorRef.current || null,
        // FIX 2: use the params passed in, not the stale savedSort snapshot.
        // For initial load (loadMore=false, key=null) fall back to savedSort.
        order: key ?? (savedSort ? JSON.parse(savedSort).key : "default"),
        reversed:
          key !== null
            ? reverse
            : savedSort
              ? JSON.parse(savedSort).direction
              : false,
        stageIds: STAGE_IDS,
        archiveStatuses: ARCHIVE_STATUSES,
        limit: limit,
        filterFields: filters,
        searchFields: apiSearchFields,
      });
      // only checks that Items array actually exists
      if (result?.Items !== undefined) {
        setTotalInvoiceCount(result.TotalItem ?? 0);
        setInvoices((prev) => {
          if (!loadMore) return result.Items;
          const existingIds = new Set(prev.map((item) => item.id));
          const newItems = result.Items.filter(
            (item: any) => !existingIds.has(item.id),
          );
          return [...prev, ...newItems];
        });
        cursorRef.current = result.NextCursor || "";
        setHasMore(result.HasMore ?? false);
      }
      const fullresult = await GetInvoicesService_NEW({
        userId: selectedClient_userID,
        after: null, // <-- always null for full fetch
        order: key ?? (savedSort ? JSON.parse(savedSort).key : "default"),
        reversed:
          key !== null
            ? reverse
            : savedSort
              ? JSON.parse(savedSort).direction
              : false,
        stageIds: STAGE_IDS,
        archiveStatuses: ARCHIVE_STATUSES,
        limit: result.TotalItem || totalInvoiceCount, // <-- FIX: use fresh result, not stale state
        filterFields: filters,
        searchFields: apiSearchFields,
      });
      setFullInvoices(fullresult?.Items || []);
      setFullInvoices(fullresult.Items);
    } catch (error) {
      console.error("Inbox fetch error:", error);
    } finally {
      if (!loadMore) setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // reset cursor for new search
    cursorRef.current = "";

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      FetchInvoices(
        false,
        false,
        null,
        activeFilters,
        value ? { searchTerm: value } : null,
      );
    }, 500); // 500ms delay
  };
  const handleAdvancedApply = (searchFields: any) => {
    setActiveAdvancedSearch(searchFields);
    cursorRef.current = "";
    FetchInvoices(false, false, null, activeFilters, toInvoiceSearchPayload(searchFields));
    setIsOpenAdvance(false);
  };

  const handleAdvancedReset = () => {
    setActiveAdvancedSearch(null);
    cursorRef.current = "";
    advancedSearchRef.current?.resetForm();
    FetchInvoices(false, false, null, activeFilters, null);
  };

  // --- Actions ---

  // Load next page when user scrolls to bottom — preserves current sort order
  const loadMoreInvoicesOnScroll = async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    await FetchInvoices(
      true,
      savedSort ? JSON.parse(savedSort).direction : false,
      savedSort ? JSON.parse(savedSort).key : null,
      activeFiltersRef.current,
      activeAdvancedSearchRef.current,
    );
    setIsLoadingMore(false);
  };

  // Sort — resets cursor and clears list before fetching sorted results
  // Sort — resets cursor and clears list before fetching sorted results
  const sortInvoices = async (
    reverse: boolean,
    key: keyof typeof InvoiceFieldMap | null,
  ) => {
    cursorRef.current = "";
    sortKeyRef.current = key;
    sortReverseRef.current = reverse;

    const newSettings = { key, direction: reverse };

    // Save to State
    setSortSettings(newSettings);

    // Save to LocalStorage
    localStorage.setItem("sortSettings", JSON.stringify(newSettings));

    await FetchInvoices(
      false,
      reverse,
      key,
      activeFiltersRef.current,
      activeAdvancedSearchRef.current,
    );
  };
  // Full refresh — resets everything back to default state
  const refreshInvoices = async () => {
    cursorRef.current = "";
    sortKeyRef.current = null;
    sortReverseRef.current = false;
    setHasMore(true);
    // setInvoices([]);

    const sortDir = savedSort ? JSON.parse(savedSort).direction : false;
    const sortKey = savedSort ? JSON.parse(savedSort).key : null;

    await FetchInvoices(
      false,
      sortDir,
      sortKey,
      activeFilters,
      activeAdvancedSearch,
      invoices.length || 100,
    );
  };

  // Apply filters — resets cursor and sort, fetches with new filter values
  const handleApplyFilters = (filterValues: any) => {
    cursorRef.current = "";
    sortKeyRef.current = null; // ← reset sort
    sortReverseRef.current = false;
    setInvoices([]);
    setActiveFilters(filterValues);
    setIsFilterPopupOpen(false);
    FetchInvoices(false, false, null, filterValues);
  };

  // --- Guards ---
  if (!selectedClient.selectedClient?.userId) {
    return <h1>Please select a client to view their invoices.</h1>;
  }

  if (loading && invoices.length === 0) return <Loader />;

  const handleRemoveFilter = (key: keyof InvoiceFilterFieldsPayload) => {
    const updated = { ...activeFilters, [key]: null };
    setActiveFilters(updated);
    setLoading(true);
    // FetchInvoices is triggered by the activeFilters useEffect automatically
  };

  const handleResetFilters = () => {
    setActiveFilters(null);
    setActiveAdvancedSearch(null);
    cursorRef.current = "";
    advancedSearchRef.current?.resetForm();
    setLoading(true);
    FetchInvoices(false, false, null, null, null);
  };
  const handleAdvancedRemove = (updated: any) => {
    setActiveAdvancedSearch(updated);
    cursorRef.current = "";
    setInvoices([]);
    FetchInvoices(false, false, null, activeFilters, toInvoiceSearchPayload(updated)); // Inbox/Review
  };

  const hasActiveSearch =
    searchTerm ||
    activeFilters ||
    (activeAdvancedSearch &&
      Object.values(activeAdvancedSearch).some((v) => v !== "" && v !== null));

  const isGenuinelyEmpty =
    invoices.length === 0 && !hasActiveSearch && !loading;
  const showToolbar = !isGenuinelyEmpty;

  return (
    <>
      <div>
        {invoiceView ? (
          // Invoice detail view
          <InvoiceView
            setInvoiceView={setInvoiceView}
            refreshInvoices={refreshInvoices}
            allInvoices={invoices}
            sortSettings={sortSettings}
            fullinvoices={fullinvoices}
            totalInvoiceCount={totalInvoiceCount}
          />
        ) : (
          // Invoice list view
          <>
            {/* Toolbar */}
            <div className="selected-client-toolbar">
              <SelectedClientCard />

              {showToolbar && (
                <div className="selected-client-toolbar__actions">
                  {
                    <ColumnSettings
                      isOpen={isOpen}
                      onClose={() => setIsOpen(false)}
                      onApply={(cols) => setSelectedColumns(cols)}
                    />
                  }
                  {
                    <InvoiceFilter
                      activeFilters={activeFilters}
                      isOpen={isFilterPopupOpen}
                      onClose={() => setIsFilterPopupOpen(false)}
                      onApply={handleApplyFilters}
                    />
                  }
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "5px 8px", // inner spacing
                      width: "300px", // adjust width
                      background: "#fff",
                    }}
                  >
                    {/* Search Icon */}
                    <FiSearch size={18} style={{ color: "#474646" }} />
                    {/* Input Field */}
                    <input
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        fontSize: "var(--font-size-base)",
                      }}
                    />

                    {/* Advanced Button */}
                    <button
                      type="button"
                      onClick={() => setIsOpenAdvance(true)}
                      className="advanced-btn"
                    >
                      Advanced
                      <FiChevronDown style={{ marginLeft: "4px" }} />
                    </button>
                  </div>
                  <AdvancedSearch
                    setIsOpenAdvance={isOpenAdvance}
                    onClose={() => setIsOpenAdvance(false)}
                    FetchInvoices={FetchInvoices}
                    onApply={handleAdvancedApply}
                    formRef={advancedSearchRef}
                    activeSearchFields={activeAdvancedSearch}
                  />

                  <FilterBtn
                    onClick={() => setIsFilterPopupOpen(true)}
                    disabled={loading}
                    icon={MdOutlineFilterAlt}
                  />

                  <FilterBtn
                    onClick={() => setIsOpen(true)}
                    disabled={loading}
                    Icon={IoSettingsSharp}
                  />
                </div>
                )}
              </div>
              <div className="tm-row">
                <ToolsMenu
                  selectedIds={selectedIds}
                  invoices={invoices}
                  userId={selectedClient_userID}
                  onMerge={handleMerge}
                  onFlag={handleFlag}
                  onUnflag={handleUnflag}
                  onDelete={handleDelete}
                  disabled={loading}
                  screen="inbox"
                  bulkEditOptions={bulkEditOptions}
                  onBulkEditComplete={handleBulkEditComplete}
                  onViewFailedItems={handleViewFailedItems}
                />
                {failedView && (
                  <span style={{ marginLeft: 12, fontSize: "var(--font-size-md)" }}>
                    Showing items that could not be updated.{" "}
                    <button type="button" onClick={handleShowAllAgain}>Show all</button>
                  </span>
                )}
            </div>
            {showToolbar && (
              <ActiveFilterBar
                activeFilters={activeFilters}
                activeAdvancedSearch={activeAdvancedSearch}
                onRemove={handleRemoveFilter}
                onReset={handleResetFilters}
                onAdvancedReset={handleAdvancedReset}
                onAdvancedRemove={handleAdvancedRemove}
              />
            )}

            {loading ? (
              <Loader />
            ) : invoices.length === 0 ? (
              <h1
                style={{
                  textAlign: "center",
                  marginTop: "40px",
                  color: "#888",
                }}
              >
                {(activeAdvancedSearch &&
                  Object.values(activeAdvancedSearch).some(
                    (v) => v !== "" && v !== null,
                  )) ||
                activeFilters ||
                searchTerm
                  ? "No invoices match your search or filter criteria."
                  : "No invoices found for this client."}
              </h1>
            ) : (
              <InboxInvoiceList
                loadMoreInvoicesOnScroll={loadMoreInvoicesOnScroll}
                setInvoiceView={setInvoiceView}
                invoicesDataSource={invoices}
                totalInvoiceCount={totalInvoiceCount}
                isLoadingMore={isLoadingMore}
                sortInvoices={sortInvoices}
                selectedColumns={selectedColumns}
                selectedIds={selectedIds}
                onRowSelect={handleRowSelect}
                onSelectAll={handleSelectAll}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Inbox;
